import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../App';
import { StatutoryExportBar } from './StatutoryExportBar';

const rm = (v) => `${Number(v || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const COLS = [
  ['wages', 'Wages'], ['epf_employee', 'EPF EE'], ['epf_employer', 'EPF ER'],
  ['socso_employee', 'SOCSO EE'], ['socso_employer', 'SOCSO ER'], ['eis_employee', 'EIS EE'], ['eis_employer', 'EIS ER'],
  ['skbkk_employee', 'SKBKK'], ['pcb', 'PCB'],
];

function defaultPeriod(payslips, filterMonth, filterYear) {
  if (filterMonth !== 'all' && filterYear !== 'all') return { month: Number(filterMonth), year: Number(filterYear) };
  const latest = [...payslips].sort((a, b) => (b.year - a.year) || (b.month - a.month))[0];
  if (latest) return { month: latest.month, year: latest.year };
  const d = new Date();
  return { month: d.getMonth() + 1, year: d.getFullYear() };
}

export const StatutoryContributions = ({ payslips, filterMonth, filterYear }) => {
  const initial = useMemo(() => defaultPeriod(payslips, filterMonth, filterYear), [payslips, filterMonth, filterYear]);
  const [month, setMonth] = useState(initial.month);
  const [year, setYear] = useState(initial.year);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setMonth(initial.month); setYear(initial.year); }, [initial]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    axios.get(`${API}/payroll/contributions?month=${month}&year=${year}`)
      .then((res) => { if (active) setData(res.data); })
      .catch((err) => { if (active) { setData(null); toast.error(err.response?.data?.detail || 'Failed to load contributions'); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [month, year]);

  const years = useMemo(() => {
    const ys = new Set(payslips.map((p) => p.year));
    ys.add(new Date().getFullYear());
    ys.add(year);
    return [...ys].sort((a, b) => b - a);
  }, [payslips, year]);

  const rows = data?.rows || [];
  const totals = data?.totals || {};

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden" data-testid="statutory-contributions-card">
      <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Statutory Contributions by Employee</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {rows.length} employee(s) · {totals.finalized || 0} finalized · {MONTHS[month - 1]} {year}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))} data-testid="statutory-month-select"
            className="h-9 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 text-sm text-slate-900 dark:text-slate-100">
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="statutory-year-select"
            className="h-9 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 text-sm text-slate-900 dark:text-slate-100">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>
      <div className="p-4 border-b border-slate-200 dark:border-slate-700">
        <StatutoryExportBar month={month} year={year} readiness={data?.readiness || {}} kwspMonth={data?.kwsp_contribution_month} disabled={loading || rows.length === 0} />
      </div>
      {data?.warnings?.length > 0 && (
        <ul className="px-4 py-3 text-xs text-amber-800 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-200 space-y-0.5 border-b border-amber-200 dark:border-amber-800" data-testid="statutory-data-warnings">
          {data.warnings.map((w) => <li key={w}>• {w}</li>)}
        </ul>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="statutory-contributions-table">
          <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Employee</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">IC / Passport</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">KWSP No · TIN</th>
              {COLS.map(([k, label]) => (
                <th key={k} className="px-3 py-2 text-right text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase whitespace-nowrap">{label}</th>
              ))}
              <th className="px-3 py-2 text-center text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
            {loading && <tr><td colSpan={COLS.length + 4} className="px-3 py-6 text-center text-slate-500">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={COLS.length + 4} className="px-3 py-6 text-center text-slate-500" data-testid="statutory-empty">No payslips for {MONTHS[month - 1]} {year}. Process payroll first.</td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.payslip_id} className="hover:bg-slate-50 dark:hover:bg-slate-700" data-testid={`statutory-row-${r.employee_id}`}>
                <td className="px-3 py-2 text-slate-900 dark:text-slate-100">
                  <div className="font-medium">{r.employee_name}</div>
                  <div className="text-xs text-slate-500">{r.employee_id}{r.final_month ? ' · Leaver (final month)' : ''}</div>
                </td>
                <td className="px-3 py-2 text-slate-700 dark:text-slate-300 whitespace-nowrap">{r.ic_number || r.passport_number || <span className="text-rose-600">missing</span>}</td>
                <td className="px-3 py-2 text-xs text-slate-700 dark:text-slate-300 whitespace-nowrap">{r.epf_number || '—'} · {r.tax_number || '—'}</td>
                {COLS.map(([k]) => (
                  <td key={k} className="px-3 py-2 text-right tabular-nums text-slate-900 dark:text-slate-100">{rm(r[k])}</td>
                ))}
                <td className="px-3 py-2 text-center">
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${r.finalized ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'}`}>
                    {r.finalized ? 'Finalized' : 'Draft'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="bg-slate-50 dark:bg-slate-900 font-bold">
              <tr data-testid="statutory-totals-row">
                <td className="px-3 py-2 text-slate-900 dark:text-slate-100" colSpan={3}>TOTAL ({totals.employees} employees)</td>
                {COLS.map(([k]) => (
                  <td key={k} className="px-3 py-2 text-right tabular-nums text-slate-900 dark:text-slate-100">{rm(totals[k])}</td>
                ))}
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};
